import React, { useEffect, useState } from 'react';
import { InfoHint } from '../InfoHint';
import './KPICard.css';

export interface KPITrend {
  direction: 'up' | 'down';
  value: string; // e.g. "12%"
}

interface KPICardProps {
  title: string;
  value: string | number | null;
  subtitle?: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'success' | 'warning' | 'info';
  loading?: boolean;
  /** How to format numeric value: score (e.g. 5.93), length (e.g. 1,234), or text (as-is) */
  format?: 'score' | 'length' | 'text';
  /** Optional reference value (e.g., org-level metric when viewing user-specific data) */
  referenceValue?: string | number | null;
  /** Label for the reference value */
  referenceLabel?: string;
  /** Optional informational note – shown in a clickable popover */
  note?: string;
  /** Optional trend vs previous period (e.g. { direction: 'up', value: '5%' }) */
  trend?: KPITrend | null;
}

const DURATION_MS = 1200;

function useCountUp(
  target: string | number | null,
  enabled: boolean
): string | number | null {
  const [display, setDisplay] = useState<string | number | null>(enabled ? (typeof target === 'number' ? 0 : target) : target);
  useEffect(() => {
    if (!enabled || target === null || target === undefined) {
      setDisplay(target ?? '—');
      return;
    }
    if (typeof target !== 'number') {
      setDisplay(target);
      return;
    }
    const start = 0;
    const end = target;
    const startTime = performance.now();
    let rafId: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / DURATION_MS, 1);
      const eased = 1 - (1 - t) * (1 - t);
      const current = start + (end - start) * eased;
      setDisplay(Number(current.toFixed(2)));
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, enabled]);
  return display;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  variant = 'primary',
  loading = false,
  referenceValue,
  referenceLabel = 'Org avg',
  note,
  trend,
  format = 'text',
}) => {
  const displayValue = value === null || value === undefined ? '—' : value;
  const isNumeric = typeof displayValue === 'number' && !Number.isNaN(displayValue);
  const countUpValue = useCountUp(displayValue, !loading && isNumeric);
  const rawToShow = loading ? null : (isNumeric ? countUpValue : displayValue);
  const valueToShow =
    rawToShow === null || rawToShow === undefined
      ? '—'
      : typeof rawToShow === 'number'
        ? format === 'score'
          ? rawToShow.toFixed(2)
          : format === 'length'
            ? Math.round(rawToShow).toLocaleString()
            : String(rawToShow)
        : rawToShow;
  const isEmailLike = typeof valueToShow === 'string' && valueToShow.includes('@');
  const isPhrase =
    typeof valueToShow === 'string' &&
    !isNumeric &&
    !isEmailLike &&
    (valueToShow.includes(' ') || valueToShow.includes('/'));
  const hasReference = referenceValue !== null && referenceValue !== undefined;

  return (
    <div className={`kpi-card kpi-card--${variant}`}>
      {icon ? <div className="kpi-card__icon-container">{icon}</div> : null}
      <div className="kpi-card__content">
        <div className="kpi-card__title-row">
          <h3 className="kpi-card__title">{title}</h3>
          {note && <InfoHint text={note} />}
        </div>
        {loading ? (
          <div className="kpi-card__skeleton" />
        ) : (
          <>
            <p
              className={`kpi-card__value${isEmailLike ? ' kpi-card__value--long' : ''}${isPhrase ? ' kpi-card__value--phrase' : ''}`}
              title={typeof valueToShow === 'string' ? valueToShow : undefined}
            >
              {valueToShow}
            </p>
            {trend && (
              <span className={`kpi-card__trend kpi-card__trend--${trend.direction}`}>
                {trend.direction === 'up' ? '↑' : '↓'} {trend.value}
              </span>
            )}
            {subtitle && <span className="kpi-card__subtitle">{subtitle}</span>}
            {hasReference && (
              <div className="kpi-card__reference">
                <span className="kpi-card__reference-label">{referenceLabel}:</span>
                <span className="kpi-card__reference-value">{referenceValue}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KPICard;
